import React, { useRef, lazy } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Image as ImageIcon } from 'lucide-react';
// ============================================================================
// REUSABLE PIECES
// ============================================================================
type PhotoData = {
  src?: string;
  alt: string;
  aspect?: string;
  /**
   * Approximate rendered width of this image at the largest (desktop) breakpoint,
   * in CSS pixels. Used to build a resolution-matched srcSet so the CDN serves a
   * crisp, appropriately-sized image (and a 2x variant for retina displays)
   * instead of an arbitrarily downscaled default.
   */
  displayWidth?: number;
  /**
   * How the image should fit its slot. Defaults to 'cover' (fills slot, may
   * crop). Use 'contain' when the full frame must remain visible (e.g. group
   * shots where cropping would remove a subject).
   */
  fit?: 'cover' | 'contain';
  /**
   * CSS object-position value. Lets us steer the crop when using 'cover' (e.g.
   * "center top" to keep faces visible).
   */
  objectPosition?: string;
  /**
   * When > 0, the image drifts subtly as it scrolls through the viewport
   * (parallax). The value is the drift amount in % (~8 for lead images, ~5 for
   * supporting ones); 0 disables it. Capped at 10 so the overscan never reveals
   * an edge.
   */
  parallax?: number;
  /**
   * Frame silhouette. 'organic' (default) = uneven, hand-shaped corners that tie
   * the frames to the bio-fusion aesthetic; 'soft' = an even rounded rectangle.
   */
  shape?: 'soft' | 'organic';
};
// Magic Patterns CDN supports on-the-fly image transforms via query params.
// We request high-quality WebP at explicit widths so images render sharp.
function cdnUrl(src: string, width: number, quality = 88) {
  // Only transform Magic Patterns CDN assets; leave anything else untouched.
  if (!src.includes('cdn.magicpatterns.com')) return src;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}width=${Math.round(width)}&quality=${quality}&format=webp`;
}
// Candidate widths used to build a comprehensive srcSet so the browser can pick
// the sharpest variant for any combination of viewport width × device pixel
// ratio (covers small phones up to 4K desktop + retina laptops).
const SRC_SET_WIDTHS = [480, 768, 1024, 1280, 1600, 2000, 2400];
function Photo({
  src,
  alt,
  aspect = 'aspect-[4/3]',
  displayWidth = 700,
  fit = 'cover',
  objectPosition,
  parallax = 0,
  shape = 'organic'
}: PhotoData) {
  // Track this image's progress through the viewport (0 = just entering at the
  // bottom, 1 = just leaving at the top) to drive a subtle parallax drift.
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const p = Math.min(Math.max(parallax, 0), 10);
  const parallaxY = useTransform(scrollYProgress, [0, 1], [`${-p}%`, `${p}%`]);
  // Frame silhouette — ties the photo frames to the bio-fusion aesthetic.
  const borderRadius = shape === 'organic' ? '1.25rem 2.75rem 1.25rem 2.75rem' : '1rem';
  // Cap the upper bound at 2.5× the intended slot to avoid serving huge files
  // for small tiles, but never below 1600 so retina + zoom stay sharp.
  const maxWidth = Math.max(1600, Math.min(displayWidth * 2.5, 2400));
  const widths = SRC_SET_WIDTHS.filter(w => w <= maxWidth);
  // Fallback `src` uses a mid-high variant so non-srcSet browsers still get a
  // crisp image (most are retina/HiDPI).
  const fallbackWidth = Math.min(Math.max(displayWidth * 2, 1280), maxWidth);
  const srcSet = src ? widths.map(w => `${cdnUrl(src, w)} ${w}w`).join(', ') : undefined;
  const image = <img src={cdnUrl(src as string, fallbackWidth)} srcSet={srcSet} sizes={`(max-width: 640px) 100vw, (max-width: 1024px) 58vw, ${displayWidth}px`} alt={alt} loading="lazy" decoding="async" onError={event => {
    const imageElement = event.currentTarget;
    if (imageElement.dataset.originalSource === 'loaded') return;
    imageElement.dataset.originalSource = 'loaded';
    imageElement.removeAttribute('srcset');
    imageElement.src = src as string;
  }} className={`w-full h-full ${fit === 'contain' ? 'object-contain' : 'object-cover'} transition-transform duration-700 group-hover:scale-105`} style={{
    imageRendering: 'auto',
    objectPosition
  }} data-id="element-1759" data-mp="2173gmi0i4ttg" />;
  return <div ref={containerRef} style={{
    borderRadius
  }} className={`${aspect} w-full bg-myceliumWhite/5 border border-earthClay/25 overflow-hidden relative group`} data-id="element-1760" data-mp="3clh9iowpdlo9">
      {src ? p > 0 ? <motion.div className="absolute inset-x-0 -top-[15%] -bottom-[15%] will-change-transform" style={{
      y: parallaxY
    }} data-id="element-1761" data-mp="33lu8i47vyhvq">
            {image}
          </motion.div> : image : <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center" data-id="element-1762" data-mp="2maxb6k217k9y">
          <div className="absolute inset-0 bg-[#0D2B24]/30 group-hover:bg-[#0D2B24]/50 transition-colors duration-500" data-id="element-1763" data-mp="2hcvrpy0p1ixy" />
          <ImageIcon className="w-6 h-6 text-earthClay/50 mb-3 relative z-10" data-id="element-1764" data-mp="2w21rpo7cvd6w" />
          <p className="text-earthClay/70 text-xs md:text-sm font-medium relative z-10 max-w-xs leading-relaxed" data-id="element-1765" data-mp="2r87aqsj4xcbi">
            [{alt}]
          </p>
        </div>}
      {/* Bio-Luminescent Teal hover edge */}
      <div style={{
      borderRadius
    }} className="absolute inset-0 ring-1 ring-bioLuminescentTeal/0 group-hover:ring-bioLuminescentTeal/30 transition-all duration-500 pointer-events-none" data-id="element-1766" data-mp="303wqom1tdehi" />
    </div>;
}
function SectionHeader({
  title,
  tagline
}: {
  title: string;
  tagline: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.8
  }} className="mb-12 md:mb-16" data-id="element-1767" data-mp="1fmicopylbijg">
      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-myceliumWhite tracking-tight mb-4 leading-[1.1]" data-id="element-1768" data-mp="2ea9fca8suwu7">
        {title}
      </h2>
      <p className="text-earthClay text-lg md:text-xl font-serif italic mb-6 max-w-2xl" data-id="element-1769" data-mp="2jb6zutha82yv">
        {tagline}
      </p>
      <div className="h-px w-full bg-gradient-to-r from-vitalityTeal/60 via-vitalityTeal/30 to-transparent" data-id="element-1770" data-mp="2o75rcpxwq13i" />
    </motion.div>;
}
function SubEntry({
  name,
  caption,
  children
}: {
  name: string;
  caption: string;
  children: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.8
  }} className="mb-16 md:mb-20" data-id="element-1771" data-mp="31zvfp10wri3s">
      <div className="mb-5 md:mb-7 max-w-3xl" data-id="element-1772" data-mp="10csjhk1m3jke">
        <div className="flex items-center gap-3 mb-2" data-id="element-1773" data-mp="vce9vxkd41k9">
          <div className="h-px w-8 bg-vitalityTeal/50" data-id="element-1774" data-mp="vatek5q84elc" />
          <h3 className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-vitalityTeal" data-id="element-1775" data-mp="qc8eq7evvwxf">
            {name}
          </h3>
        </div>
        <p className="text-myceliumWhite/80 text-base md:text-lg leading-relaxed font-light" data-id="element-1776" data-mp="lg1k3arxu7us">
          {caption}
        </p>
      </div>
      {children}
    </motion.div>;
}
// Editorial "beat" — an asymmetric composition that alternates sides down the page.
// A dominant lead image pairs with an optional smaller, vertically-staggered support
// image, while the eyebrow + caption sit in the adjacent whitespace. Varied scale and
// alternating sides give the page an organic, magazine-spread rhythm instead of a
// uniform grid.
function EditorialBeat({
  name,
  caption,
  lead,
  support,
  flip = false,
  variant = 'wide'
}: {
  name?: string;
  caption?: string;
  lead: PhotoData;
  support?: PhotoData;
  flip?: boolean;
  variant?: 'wide' | 'tall';
}) {
  const hasText = Boolean(name || caption);
  // The media block: a lead + vertically-staggered support pair, or a single
  // image. `flip` swaps the left/right order so pairs alternate down the page.
  const media = support ? <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 sm:gap-4 md:gap-6 items-start" data-id="element-1777" data-mp="11cjyvo6u7c7t">
      <div className={`w-full sm:col-span-7 ${flip ? 'sm:order-2' : ''}`} data-id="element-1778" data-mp="1p2o2hvk0zbyt">
        <Photo {...lead} parallax={lead.parallax ?? 8} data-id="element-1779" data-mp="1t2geyjcji29g" />
      </div>
      <div className={`w-full max-w-md mx-auto sm:max-w-none sm:col-span-5 sm:mx-0 sm:mt-16 md:mt-24 ${flip ? 'sm:order-1' : ''}`} data-id="element-1780" data-mp="1f3yssq2a8xtn">
        <Photo {...support} parallax={support.parallax ?? 5} data-id="element-1781" data-mp="1k18hu4lrf7gy" />
      </div>
    </div> : <div className={variant === 'tall' ? `max-w-xs sm:max-w-sm ${flip ? 'lg:mr-auto' : 'lg:ml-auto'}` : 'w-full'} data-id="element-1782" data-mp="3ns4innieue2">
      <Photo {...lead} parallax={lead.parallax ?? 8} data-id="element-1783" data-mp="13lz0bj8yvve" />
    </div>;
  return <motion.div initial={{
    opacity: 0,
    y: 48
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-120px'
  }} transition={{
    duration: 0.9,
    ease: [0.16, 1, 0.3, 1]
  }} className="mb-28 md:mb-40 lg:mb-48" data-id="element-1784" data-mp="3saezfd67uqyv">
      {hasText ? <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center" data-id="element-1785" data-mp="3dls96gf1wesv">
          {/* Text set into the whitespace beside the image */}
          <div className={`lg:col-span-4 ${flip ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-1786" data-mp="3nbvcpajstb5g">
            {name && <div className="flex items-center gap-3 mb-4" data-id="element-1787" data-mp="3ijtt13kawi5e">
                <div className="h-px w-10 bg-vitalityTeal/60" data-id="element-1788" data-mp="2uuiyi3tjnjzp" />
                <h3 className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-vitalityTeal" data-id="element-1789" data-mp="2usy1m7jrboyo">
                  {name}
                </h3>
              </div>}
            {caption && <p className="font-serif text-myceliumWhite/90 text-xl md:text-2xl leading-relaxed" data-id="element-1790" data-mp="37sd2eykkfoa6">
                {caption}
              </p>}
          </div>

          {/* Media */}
          <div className={`lg:col-span-8 ${flip ? 'lg:order-1' : 'lg:order-2'}`} data-id="element-1791" data-mp="2yropk76wrtyv">
            {media}
          </div>
        </div> : media}
    </motion.div>;
}
// ============================================================================
// PAGE
// ============================================================================
export function Stories() {
  const pageRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: pageRef,
    offset: ['start start', 'end end']
  });
  const rootLineScale = useTransform(scrollYProgress, [0.1, 0.95], [0, 1]);
  // Vertical position of the glowing "growth tip" that leads the root line as it
  // fills — tracks the same scroll range as the fill so it rides the growing front.
  const rootTipTop = useTransform(scrollYProgress, [0.1, 0.95], ['0%', '100%']);
  return <div ref={pageRef} className="min-h-screen bg-[#0A1111] text-myceliumWhite selection:bg-vitalityTeal selection:text-[#0A1111] relative" data-id="element-1792" data-mp="qlwcaceoetr1">
      {/* ====================================================================
                                             NARRATIVE HEADER (HERO)
                                             ==================================================================== */}
      <section className="relative min-h-[80vh] flex flex-col justify-center overflow-hidden pt-32 pb-20" data-id="element-1793" data-mp="bundpdsspy1r">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D2B24] to-[#0A1111] z-0" data-id="element-1794" data-mp="gngh9ycj30c9" />

        {/* Subtle Mycelium SVG Texture */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" data-id="element-1795" data-mp="3kayxh987io08">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1796" data-mp="3k8w209oac2o4">
            <filter id="noise" data-id="element-1797" data-mp="rexceedr0m4m">
              <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch" data-id="element-1798" data-mp="mhf5etjakgz9" />
            </filter>
            <rect width="100%" height="100%" filter="url(#noise)" data-id="element-1799" data-mp="w774o12c5ya2" />
          </svg>
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-1800" data-mp="w9kvfxpzgobt">
            <path d="M0,0 Q50,50 100,100 M100,0 Q50,50 0,100 M50,0 Q25,50 50,100" stroke="#2BC7B7" strokeWidth="0.1" fill="none" data-id="element-1801" data-mp="3jkwefi5y5gv" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-1802" data-mp="chwpco6i5fch">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-serif text-6xl md:text-8xl lg:text-9xl text-myceliumWhite mb-8 tracking-tight" data-id="element-1803" data-mp="1svwk7kqhqesv">
            Stories.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-xl md:text-2xl text-myceliumWhite/80 max-w-[640px] leading-relaxed font-light" data-id="element-1804" data-mp="1rfskimesbqxq">
            This is what it looks like to build at the intersection of nature's
            intelligence and the most important moment in the history of
            innovation. We cover ground. We meet founders where they are. We are
            telling the stories that matter.
          </motion.p>
        </div>
      </section>

      {/* Vertical teal "root line" — a growing spine that threads the beats
              together as you scroll (desktop). Sits above the section backgrounds so
              it stays visible, in the left gutter so it never overlaps the images. */}
      <div className="hidden lg:block absolute left-[calc((100%-min(100%,80rem))/2+1rem)] top-[80vh] bottom-24 w-px pointer-events-none z-20" data-id="element-1805" data-mp="10b6ntzbpsrdg">
        {/* Faint full-length track */}
        <div className="absolute inset-0 bg-myceliumWhite/10" data-id="element-1806" data-mp="1syapbublmnkh" />
        {/* Teal fill that grows from the top as you scroll */}
        <motion.div className="absolute inset-x-0 top-0 h-full origin-top bg-gradient-to-b from-vitalityTeal via-vitalityTeal/70 to-bioLuminescentTeal" style={{
        scaleY: rootLineScale
      }} data-id="element-1807" data-mp="1o69emcu2x4wk" />

        {/* Glowing growth tip that rides the front of the fill */}
        <motion.div className="absolute -left-[3.5px] w-2 h-2 rounded-full bg-bioLuminescentTeal shadow-[0_0_12px_3px_rgba(77,231,212,0.6)] -translate-y-1/2" style={{
        top: rootTipTop
      }} data-id="element-1808" data-mp="2bw3qos6kqpfr" />
      </div>

      {/* ====================================================================
                                             SECTION 1 — FOUNDERS BUILDING WHAT'S NEXT
                                             ==================================================================== */}
      <section className="relative py-24 md:py-32 bg-[#0A1111] z-10" data-id="element-1809" data-mp="3768mf30ror4o">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1810" data-mp="374fd1fyrrssz">
          <SectionHeader title="Founders Building What's Next" tagline="Four companies. Three sectors. One thesis." data-id="element-1811" data-mp="3byjmc5viccx8" />

          {/* BOPA — landscape lead + staggered support, text left */}
          <EditorialBeat name="BOPA Precision Agriculture" caption="Visiting Sarah Hinkley and the BOPA team at the workshop." lead={{
          src: 'https://cdn.magicpatterns.com/uploads/cLmgqCfC8cxzujunk83mER/Nat_Sarah_Firefly_Upscaler_4x_scale.jpg',
          alt: 'Vitality team member with Sarah Hinkley at the BOPA workshop',
          aspect: 'aspect-[4/3]',
          displayWidth: 780
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/vmGn8QGEYsM9m2GSdNzSrD/BOPA_(1).jpg',
          alt: 'BOPA founders with an autonomous nano-tractor under stage lighting',
          aspect: 'aspect-[3/2]',
          displayWidth: 420
        }} data-id="element-1812" data-mp="3lx95s331y5fl" />

          {/* TierraSphere — single tall portrait, text right */}
          <EditorialBeat name="TierraSphere" caption="Cheering on Marcela Flores, founder of TierraSphere, in Bogotá, Colombia." flip variant="tall" lead={{
          src: 'https://cdn.magicpatterns.com/uploads/wjj51wvg9przKjuNg3zEYm/NatMarcela.png',
          alt: 'Marcela Flores and Natalya Wallin at an industry event in Bogotá',
          aspect: 'aspect-[3/4]',
          objectPosition: 'center 35%',
          displayWidth: 460
        }} data-id="element-1813" data-mp="2ei3tdzcvreye" />

          {/* ReefCycle — landscape lead + staggered support, text left */}
          <EditorialBeat name="ReefCycle" caption="Mary Lempres and Shannon Parker are turning coral reef biology into next-generation sustainable materials. Nature's most resilient structures, reimagined for modern manufacturing." lead={{
          src: 'https://cdn.magicpatterns.com/uploads/2f6Ke89n2ypmUbLkM3w1ot/ShannonVitality.png',
          alt: 'ReefCycle and Vitality team members at an indoor event',
          aspect: 'aspect-[4/3]',
          displayWidth: 780
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/1gkUyw3kBaWn1eozKRP6w2/MaryShannon.jpg',
          alt: 'Mary Lempres and Shannon Parker outdoors by a tree',
          aspect: 'aspect-[3/2]',
          displayWidth: 420
        }} data-id="element-1814" data-mp="58crrs9oslmb" />

          {/* Mothership Materials — single tall portrait, text right */}
          <EditorialBeat name="Mothership Materials" caption="Jo Marini, founder of Mothership Materials, on revolutionizing the future of biomanufacturing at our Vital Systems event in NYC." flip variant="tall" lead={{
          src: 'https://cdn.magicpatterns.com/uploads/9RyDaoX5jnwHn8wpNUMkWz/Jo_Speaking.png',
          alt: 'Jo Marini speaking on stage at the Vital Systems event in NYC',
          aspect: 'aspect-[3/4]',
          displayWidth: 460
        }} data-id="element-1815" data-mp="2fhvwimrc28px" />
        </div>
      </section>

      {/* ====================================================================
                                             SECTION 2 — TAKING THE THESIS ON THE ROAD
                                             ==================================================================== */}
      <section className="relative py-24 md:py-32 bg-[#0A1111] z-10" data-id="element-1816" data-mp="1dnaym1dkggo2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1817" data-mp="1dlhq0gje7mdf">
          <SectionHeader title="Taking the Thesis on the Road" tagline="From emerging manager pitch to investor panel to Times Square at midnight." data-id="element-1818" data-mp="1if2rwjo3x0hw" />

          {/* On Stage — square lead + tall support, text left */}
          <EditorialBeat name="On Stage · Biomimicry + Modern Tech" caption="Taking the stage in SF as part of the Coolwater Capital Emerging Manager cohort to share Vitality's thesis." lead={{
          src: 'https://cdn.magicpatterns.com/uploads/rqyRZ4WSEnhJhozi5fYwo1/biomimicry_slide.jpg',
          alt: 'Natalya pitching on stage in a red dress with a biomimicry slide behind her',
          aspect: 'aspect-square',
          displayWidth: 640
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/qkmgwQCLZGHwohuorRcKxq/Coolwater_Cohort.png',
          alt: 'Vitality team with fellow founders outside the Coolwater Summit',
          aspect: 'aspect-[3/4]',
          objectPosition: 'center 25%',
          displayWidth: 420
        }} data-id="element-1819" data-mp="uoowqka86lj3" />

          {/* Vital Systems — titled beat, then a text-less continuation */}
          <EditorialBeat name="Vital Systems Event Series · New York" caption="Launched with BOPA co-investor Ginger Dhaliwal and REFASHIOND Ventures. Investors, founders, and changemakers gathered to discuss investing in what the world needs next — then took it to the streets of Times Square." flip lead={{
          src: 'https://cdn.magicpatterns.com/uploads/nGbNWEMbFR4GrqjfWijMtm/VitalSystems_Nyc.jpg',
          alt: 'Panel discussion at the Vital Systems NYC event with the city skyline behind',
          aspect: 'aspect-[4/3]',
          displayWidth: 640
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/bhEfLoDGH6JZDaiDR1AKuV/Nat_NYC.png',
          alt: 'Natalya on stage with microphone at Vital Systems, NYC skyline behind',
          aspect: 'aspect-[3/4]',
          objectPosition: 'center 30%',
          displayWidth: 420
        }} data-id="element-1820" data-mp="1xo897ht6q2ks" />

          <EditorialBeat lead={{
          src: 'https://cdn.magicpatterns.com/uploads/qG9q45XSg6yxqPWWYJyQxq/Nat_Ginger.png',
          alt: 'Natalya with BOPA co-investor Ginger Dhaliwal at Vital Systems NYC',
          aspect: 'aspect-[3/4]',
          objectPosition: 'center 25%',
          displayWidth: 620
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/nJGqiuEZzs1kFtFr6q5RhJ/Nat_Dev.jpg',
          alt: 'Out in Times Square at night after the Vital Systems event',
          aspect: 'aspect-[3/4]',
          objectPosition: 'center 30%',
          displayWidth: 420
        }} data-id="element-1821" data-mp="spbnh4dhrqnl" />

          {/* Duke Career Trek — single wide landscape, text left */}
          <EditorialBeat name="Duke Career Trek · Denver" caption="Hosted students from Duke's Fuqua School and Nicholas School of the Environment. The next generation of inventors and investors." lead={{
          src: 'https://cdn.magicpatterns.com/uploads/vysTMvZsRikigTwJXXKXy8/Vitality_Denver.jpg',
          alt: 'Duke Fuqua and Nicholas School student group in front of the Vitality Fund banner in Denver',
          aspect: 'aspect-[16/9]',
          displayWidth: 720
        }} data-id="element-1822" data-mp="3tl3z0f5pz5pb" />

          {/* REGENERATE — poster + portrait, both contained (no parallax), text right */}
          <EditorialBeat name="REGENERATE Conference · Santa Fe" caption="Vitality sponsored REGENERATE, learning from experts in the future of food and ag." flip lead={{
          src: 'https://cdn.magicpatterns.com/uploads/6oNvv8P4Wgh4eewMyJvnrK/Regenerate1.png',
          alt: 'REGENERATE social ecosystem signage featuring the Vitality logo',
          aspect: 'aspect-square',
          fit: 'contain',
          displayWidth: 620,
          parallax: 0
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/obyZjvsW8Jnv5St51f3ZYN/Nat_Carrie.png',
          alt: 'Natalya with a conference attendee holding an award at REGENERATE',
          aspect: 'aspect-[3/4]',
          fit: 'contain',
          displayWidth: 420,
          parallax: 0
        }} data-id="element-1823" data-mp="30xq9h24cpcf8" />
        </div>
      </section>

      {/* ====================================================================
                                             SECTION 3 — HOW WE OPERATE
                                             ==================================================================== */}
      <section className="relative pt-24 md:pt-32 pb-32 md:pb-40 bg-[#0A1111] z-10" data-id="element-1824" data-mp="z53ezk6b8kuh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1825" data-mp="z30n0ullkhn4">
          <SectionHeader title="How We Operate" tagline="From pilot projects on Governors Island to time together on the trails, the Vitality team shows up." data-id="element-1826" data-mp="143eqdzc973et" />

          {/* Intro line */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8
        }} className="text-myceliumWhite/85 text-lg md:text-xl leading-relaxed max-w-3xl mb-14 md:mb-20 font-light" data-id="element-1827" data-mp="1dw2k2l7xsr9b">
            From big events to the great outdoors, from pilot projects on
            Governors Island to time on the trails, the Vitality team shows up.
          </motion.p>

          {/* Governors Island — landscape pair */}
          <EditorialBeat lead={{
          src: 'https://cdn.magicpatterns.com/uploads/cHSTgCHj87EbCMr21nXYLz/GovIsland1.jpg',
          alt: 'Two team members in a selfie by the water on Governors Island',
          aspect: 'aspect-[4/3]',
          displayWidth: 760
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/i1vYKcBs9AHLpzJMWuz4rQ/GovIsland2.jpg',
          alt: 'Pilot-project equipment on Governors Island by the harbor',
          aspect: 'aspect-[4/3]',
          displayWidth: 460
        }} data-id="element-1828" data-mp="3h91ww4bqh5av" />

          {/* Dinner leads, waterfall as the smaller support */}
          <EditorialBeat lead={{
          src: 'https://cdn.magicpatterns.com/uploads/ezMEYpAu44zR3YPp9rcvYb/VitalityDinner.jpg',
          alt: 'Three Vitality team members at an evening dinner event',
          aspect: 'aspect-[3/4]',
          objectPosition: 'center 30%',
          displayWidth: 620
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/jDqG8Kj4rffuFp5iennsck/Hike3.jpg',
          alt: 'Two team members in front of a waterfall on a hike',
          aspect: 'aspect-[3/4]',
          displayWidth: 420
        }} data-id="element-1829" data-mp="2jofm2htx6rvb" />

          {/* Trails — landscape pair */}
          <EditorialBeat lead={{
          src: 'https://cdn.magicpatterns.com/uploads/t9x6TG8NXJQg6uQhUFQk6Y/Hike2.jpg',
          alt: 'Vitality team group photo on a rocky overlook with autumn mountains behind',
          aspect: 'aspect-[4/3]',
          displayWidth: 760
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/3M71oYRkvQtgM6KDWtZSKm/1762971799450.jpg',
          alt: 'Two team members on a mountain trail selfie under big sky',
          aspect: 'aspect-[4/3]',
          displayWidth: 460
        }} data-id="element-1830" data-mp="rswfxfqweyvp" />

          {/* Endurance — run + sunset pair */}
          <EditorialBeat lead={{
          src: 'https://cdn.magicpatterns.com/uploads/832CdRKk3kez2zQfX93YY2/Nat_run_2.jpg',
          alt: 'Natalya Wallin running on a mountain trail',
          aspect: 'aspect-[4/3]',
          displayWidth: 760
        }} support={{
          src: 'https://cdn.magicpatterns.com/uploads/6rhPpCZY8xGbKifGY9VQVe/Sunset.jpg',
          alt: 'Sunset over the mountains',
          aspect: 'aspect-[4/3]',
          displayWidth: 460
        }} data-id="element-1831" data-mp="3vivuo9ffftjl" />

          {/* Expeditions — subheader within How We Operate */}
          <div className="mt-24 md:mt-32" data-id="element-1832" data-mp="s2otc9khi8dz">
            <SubEntry name="Expeditions" caption="The spirit of exploration is alive and well." data-id="element-1833" data-mp="n2so6701wawu">
              {/* Wind River — landscape lead + portrait support, text left */}
              <EditorialBeat caption="Navigating the stunning remote backcountry on the Wind River High Route." lead={{
              src: 'https://cdn.magicpatterns.com/uploads/hjAAY5KhKGbRLHuYcxA3LN/Wind_river_2.jpg',
              alt: 'Ultralight tent at an alpine lake in the high country',
              aspect: 'aspect-[4/3]',
              displayWidth: 640
            }} support={{
              src: 'https://cdn.magicpatterns.com/uploads/no5kH2LbGoQSSEuAFov1P2/Wind_river_1.jpg',
              alt: 'Hiker descending a boulder field above a remote lake',
              aspect: 'aspect-[3/4]',
              displayWidth: 420
            }} data-id="element-1834" data-mp="38ufkzrxh0yo7" />

              {/* Red Sea + Kilimanjaro — diver + summit side by side, text right */}
              <EditorialBeat caption="Diving in the Red Sea and climbing Mt. Kilimanjaro." flip lead={{
              src: 'https://cdn.magicpatterns.com/uploads/ccF1S9YFCMcd5g4svDqraR/GPTempDownload_3.jpg',
              alt: 'Scuba diver above a coral reef in the Red Sea',
              aspect: 'aspect-[3/4]',
              displayWidth: 620
            }} support={{
              src: 'https://cdn.magicpatterns.com/uploads/jDnhMihWE1ft2i2s87bimn/IMG_1696.jpg',
              alt: "Summiting Uhuru Peak, Mt. Kilimanjaro — Africa's highest point at 5,895 meters",
              aspect: 'aspect-[3/4]',
              displayWidth: 420
            }} data-id="element-1835" data-mp="27ceqtky9ix2x" />
            </SubEntry>
          </div>
        </div>
      </section>
    </div>;
}